import { useCallback } from "react";
import {
    useGetListIrrigationEventsQueryData,
    useSetListIrrigationEventsQueryData,
} from "@/api/orval/generated/irrigation-event";
import type { IrrigationEventResponse } from "@/api/orval/generated/schemas";
import { removeCachedIrrigationEvent, replaceCachedIrrigationEvent } from "./patch-cached-irrigation-events";
import { removeEventFromMasterIndex, syncEventToMasterIndex } from "./sync-master-index-events";

export function useIrrigationEventCacheSync() {
    const patchCachedEvents = useSetListIrrigationEventsQueryData();
    const getCachedEvents = useGetListIrrigationEventsQueryData();

    /** The `_id` MasterIndex keys this record's model by, which a write has to supersede. */
    const getDocId = useCallback(
        (recordId: string) => getCachedEvents()?.items.find((item) => item.recordId === recordId)?._id,
        [getCachedEvents],
    );

    /**
     * Patches the cache after awaiting the mutation, which has already awaited the refetch its
     * invalidation triggered. This is the fallback for when that refetch fails: without it a
     * successful write is missing from the list until something else refetches it.
     */
    const commitUpdatedEvent = useCallback(
        (updated: IrrigationEventResponse, docId: string | undefined) => {
            patchCachedEvents(undefined, replaceCachedIrrigationEvent(updated));
            syncEventToMasterIndex(updated, docId);
        },
        [patchCachedEvents],
    );

    const commitRemovedEvent = useCallback(
        (recordId: string, docId: string | undefined) => {
            patchCachedEvents(undefined, removeCachedIrrigationEvent(recordId));
            removeEventFromMasterIndex(docId);
        },
        [patchCachedEvents],
    );

    return { getDocId, commitUpdatedEvent, commitRemovedEvent };
}
